import { useCallback, useEffect, useId, useMemo, useSyncExternalStore } from "react";
import { sameChatReference, type ChatReference } from "./chatReference";

/**
 * The references a person sent to the chat and hasn't sent to the agent yet (F96), per
 * project. Selecting in one work tab fills the composer in another, as selecting text in T3
 * fills its message box; a reference picked before any Thread is open waits for the next
 * composer. Kept for this browser tab only: its work tabs share it (F90), another browser
 * tab doesn't. The composer owns emptying it.
 */
const KEY_PREFIX = "dmw.chat.references.";
/** Changes, announced to every work tab in this window. */
const CHANGED = "dmw.chat.references.changed";
const NONE: readonly ChatReference[] = [];

/** The last read per project, so an unchanged tray keeps its identity between renders. */
const lastRead = new Map<string, { readonly raw: string | null; readonly references: readonly ChatReference[] }>();

function readTray(projectId: string): readonly ChatReference[] {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(KEY_PREFIX + projectId);
  } catch {
    return NONE;
  }
  const cached = lastRead.get(projectId);
  if (cached?.raw === raw) return cached.references;
  const references = raw === null ? NONE : parseTray(raw);
  lastRead.set(projectId, { raw, references });
  return references;
}

function parseTray(raw: string): readonly ChatReference[] {
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isChatReference) : NONE;
  } catch {
    return NONE;
  }
}

function isChatReference(value: unknown): value is ChatReference {
  if (typeof value !== "object" || value === null) return false;
  const { reference, name, quote } = value as Partial<Record<keyof ChatReference, unknown>>;
  return typeof reference === "string" && reference !== "" && typeof name === "string" && (quote === null || typeof quote === "string");
}

function writeTray(projectId: string, references: readonly ChatReference[]): void {
  try {
    if (references.length === 0) {
      window.sessionStorage.removeItem(KEY_PREFIX + projectId);
    } else {
      window.sessionStorage.setItem(KEY_PREFIX + projectId, JSON.stringify(references));
    }
  } catch {
    // Storage blocked: nothing reaches the chat, as with no composer at all.
  }
  window.dispatchEvent(new Event(CHANGED));
}

/** Add `reference` to the project's chat; one already there stays once. */
export function sendToChat(projectId: string, reference: ChatReference): void {
  const current = readTray(projectId);
  if (current.some((kept) => sameChatReference(kept, reference))) return;
  writeTray(projectId, [...current, reference]);
}

export function removeFromChat(projectId: string, reference: ChatReference): void {
  writeTray(projectId, readTray(projectId).filter((kept) => !sameChatReference(kept, reference)));
}

/** After a send: the references it carried leave the tray; ones added meanwhile stay. */
export function takeFromChat(projectId: string, sent: readonly ChatReference[]): void {
  writeTray(projectId, readTray(projectId).filter((kept) => !sent.some((gone) => sameChatReference(gone, kept))));
}

function subscribe(listener: () => void): () => void {
  window.addEventListener(CHANGED, listener);
  return () => { window.removeEventListener(CHANGED, listener); };
}

/** The project's references waiting for the chat, in the order they were sent. */
export function useChatReferences(projectId: string | null): readonly ChatReference[] {
  const snapshot = useCallback(() => (projectId === null ? NONE : readTray(projectId)), [projectId]);
  // A page rendered outside a browser tab has no tray: nothing waits there.
  return useSyncExternalStore(subscribe, snapshot, () => NONE);
}

/**
 * A Thread composer open in this window: where the project's references show (TR-B3-01,
 * TR-B3-03). One that takes no messages (saved, closed) still shows them, beside its Fork.
 */
export interface ChatDestination {
  readonly projectId: string;
  readonly threadId: string;
  readonly title: string;
  readonly takesMessages: boolean;
}

/** The composers open now, by the key each mounted one holds. */
const destinations = new Map<string, ChatDestination>();
const destinationListeners = new Set<() => void>();
const NO_DESTINATIONS: readonly ChatDestination[] = [];
let destinationSnapshot: readonly ChatDestination[] = NO_DESTINATIONS;

function destinationsChanged(): void {
  destinationSnapshot = [...destinations.values()];
  for (const listener of destinationListeners) listener();
}

function subscribeDestinations(listener: () => void): () => void {
  destinationListeners.add(listener);
  return () => { destinationListeners.delete(listener); };
}

/** A composer says it is open, while it is mounted. */
export function useChatDestination(destination: ChatDestination): void {
  const key = useId();
  const { projectId, threadId, title, takesMessages } = destination;
  useEffect(() => {
    destinations.set(key, { projectId, threadId, title, takesMessages });
    destinationsChanged();
    return () => {
      destinations.delete(key);
      destinationsChanged();
    };
  }, [key, projectId, threadId, title, takesMessages]);
}

/** The project's composers open in this window: where a reference sent now shows. */
export function useChatDestinations(projectId: string): readonly ChatDestination[] {
  const all = useSyncExternalStore(subscribeDestinations, () => destinationSnapshot, () => NO_DESTINATIONS);
  return useMemo(() => all.filter((destination) => destination.projectId === projectId), [all, projectId]);
}
